// T057: End Phase Handler

import type { GameState, CardDefinition } from '../../types';
import { CombatResolver } from './CombatResolver';

export interface EndPhaseResult {
  state: GameState;
  gameEnded: boolean;
  winnerId: string | null;
}

/**
 * Calculate the score for a player based on units on battlefield.
 */
function calculatePlayerScore(
  state: GameState,
  playerId: string,
  definitions: Map<string, CardDefinition>
): number {
  const sideKey = playerId === state.player1.id ? 'player1Side' : 'player2Side';
  const side = state.battlefield[sideKey];
  let score = 0;

  // Count front line units
  for (const slot of side.frontLine) {
    if (slot.unit) {
      const def = definitions.get(slot.unit.definitionId);
      score += def?.score ?? 0;
    }
  }

  // Count back line units
  for (const slot of side.backLine) {
    if (slot.unit) {
      const def = definitions.get(slot.unit.definitionId);
      score += def?.score ?? 0;
    }
  }

  return score;
}

/**
 * Determine the winner based on scores.
 */
function determineWinner(
  state: GameState,
  definitions: Map<string, CardDefinition>
): string {
  const score1 = calculatePlayerScore(state, state.player1.id, definitions);
  const score2 = calculatePlayerScore(state, state.player2.id, definitions);

  if (score1 > score2) {
    return state.player1.id;
  } else if (score2 > score1) {
    return state.player2.id;
  } else {
    // Tie: second player (non-first player) wins — player1 is always first by convention
    return state.player2.id;
  }
}

/**
 * Execute the end phase.
 * - Untap all units for the current player
 * - Check for countdown game end
 * - Clear attack declarations
 */
export function execute(
  state: GameState,
  definitions: Map<string, CardDefinition>
): EndPhaseResult {
  let newState = state;

  // Untap all units for the active player
  newState = CombatResolver.untapAllUnits(newState, newState.activePlayerId);

  // NOTE: Do NOT clear attack declarations here!
  // Attack declarations persist until the opponent's defense phase resolves them.
  // They are cleared in DefensePhaseHandler.execute() after combat resolution.

  // Check if countdown has reached 0
  if (newState.countdownTurns !== null && newState.countdownTurns <= 0) {
    const winnerId = determineWinner(newState, definitions);
    return {
      state: {
        ...newState,
        status: 'ended',
        gameEnded: true,
        winnerId,
      },
      gameEnded: true,
      winnerId,
    };
  }

  return {
    state: newState,
    gameEnded: false,
    winnerId: null,
  };
}

/**
 * Get current scores for both players.
 */
export function getScores(
  state: GameState,
  definitions: Map<string, CardDefinition>
): { player1: number; player2: number } {
  return {
    player1: calculatePlayerScore(state, state.player1.id, definitions),
    player2: calculatePlayerScore(state, state.player2.id, definitions),
  };
}

/**
 * Check if game should end this phase.
 */
export function shouldGameEnd(state: GameState): boolean {
  return state.countdownTurns !== null && state.countdownTurns <= 0;
}

// Re-export as object for backwards compatibility
export const EndPhaseHandler = {
  execute,
  getScores,
  shouldGameEnd,
};
